import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { apiClient, ApiError } from '../api/client';

export interface User {
  id: string;
  email: string;
  name: string;
  mfaEnabled: boolean;
  roles: string[];
  permissions: string[];
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  tenants: Tenant[];
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresMfa: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }

  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string;
    mfaEnabled: boolean;
    roles?: string[];
    permissions?: string[];
  } | null;
  tenant: Tenant | null;
  tenants: Tenant[];
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    tenants: [],
    isAuthenticated: false,
    isLoading: true,
    requiresMfa: false,
  });

  // Session restoration on mount
  useEffect(() => {
    let cancelled = false;

    apiClient
      .get<MeResponse>('/auth/me')
      .then((data) => {
        if (!cancelled && data?.user) {
          setState({
            user: {
              id: data.user.id,
              email: data.user.email,
              name: data.user.name,
              mfaEnabled: data.user.mfaEnabled,
              roles: data.user.roles ?? [],
              permissions: data.user.permissions ?? [],
            },
            tenant: data.tenant,
            tenants: data.tenants,
            isAuthenticated: true,
            isLoading: false,
            requiresMfa: false,
          });
        } else if (!cancelled) {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      // Login sets refresh_token cookie via HttpOnly cookie
      // AND returns accessToken which we can use for immediate follow-up
      const loginRes = await apiClient.post<{
        user: {
          id: string;
          email: string;
          name: string;
          mfaEnabled: boolean;
          roles?: string[];
          permissions?: string[];
        };
        session: { id: string; tokenHash: string; expiresAt: string };
        accessToken: string;
      }>('/auth/login', { email, password });

      // Use the user data from login + fetch tenant info
      try {
        const me = await apiClient.get<MeResponse>('/auth/me');

        if (me?.user) {
          setState({
            user: {
              id: me.user.id,
              email: me.user.email,
              name: me.user.name,
              mfaEnabled: me.user.mfaEnabled,
              roles: me.user.roles ?? [],
              permissions: me.user.permissions ?? [],
            },
            tenant: me.tenant,
            tenants: me.tenants,
            isAuthenticated: true,
            isLoading: false,
            requiresMfa: false,
          });
          return;
        }
      } catch {
        // /auth/me failed — construct state from login response
      }

      // Fallback: use user data from login response
      setState({
        user: {
          id: loginRes.user.id,
          email: loginRes.user.email,
          name: loginRes.user.name,
          mfaEnabled: loginRes.user.mfaEnabled,
          roles: loginRes.user.roles ?? [],
          permissions: loginRes.user.permissions ?? [],
        },
        tenant: null,
        tenants: [],
        isAuthenticated: true,
        isLoading: false,
        requiresMfa: false,
      });
    } catch (error) {
      // Handle MFA required (428 Precondition Required)
      if (error instanceof ApiError && error.status === 428) {
        setState((prev) => ({ ...prev, isLoading: false, requiresMfa: true }));
        return;
      }
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const verifyMfa = useCallback(async (code: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await apiClient.post<{
        user: User;
        tenant: Tenant;
        tenants: Tenant[];
      }>('/auth/mfa/verify', { code });

      setState({
        user: response.user,
        tenant: response.tenant,
        tenants: response.tenants,
        isAuthenticated: true,
        isLoading: false,
        requiresMfa: false,
      });
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore logout errors
    } finally {
      setState({
        user: null,
        tenant: null,
        tenants: [],
        isAuthenticated: false,
        isLoading: false,
        requiresMfa: false,
      });
    }
  }, []);

  const switchTenant = useCallback(async (tenantId: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await apiClient.post<{ tenant: Tenant }>(
        '/auth/switch-tenant',
        { tenantId },
      );

      setState((prev) => ({
        ...prev,
        tenant: response.tenant,
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return state.user?.permissions?.includes(permission) ?? false;
    },
    [state.user],
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      return state.user?.roles?.includes(role) ?? false;
    },
    [state.user],
  );

  const value: AuthContextType = {
    ...state,
    login,
    verifyMfa,
    logout,
    switchTenant,
    hasPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
